/* ============================================================
   CASPIAN HOME — behaviours for the caspian-* sections.
   Loaded on the home page only, deferred (layout/theme.liquid).

   The page works without this file; it adds:

     · html.cx-js     interactive layouts on (the journey deck, the
                      wipe hint). Always set.
     · html.cx-motion entrance motion on. Skipped for visitors who
                      asked for reduced motion.
     · reveals  — .is-in on .cx-rv / .cx-rail / [data-cx-bars] as
                  they scroll into view (IntersectionObserver, so
                  it works the same on iPhone and Android)
     · hero     — the SmartMop rises and grows as you scroll (GSAP
                  ScrollTrigger, already on the home page; skipped
                  if it did not load)
     · films    — play muted on screen, pause off it; fetched only
                  when near. Pause/play, "Watch with sound".
     · deck     — the journey cards: swipe/drag the top card away,
                  arrows, counter
     · wipe     — the dirty floor you clean with a finger (canvas)
     · story    — "Our story" as a phone story: bars, tap/swipe/hold
     · rails    — desktop arrows beside card rows
     · bar      — the phone action bar shows after the hero

   Click handlers are delegated from the document and registered
   once (window.__cxHome). Everything per-element is rebuilt on
   shopify:section:load, so editing in the theme editor never
   stacks duplicates.
   ============================================================ */
(function () {
  if (window.__cxHome) return;
  window.__cxHome = true;

  var REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;
  root.classList.add('cx-js');
  if (!REDUCED) root.classList.add('cx-motion');

  var cleanups = [];
  function onCleanup(fn) { cleanups.push(fn); }

  /* ---------------- reveals ---------------- */
  function reveals() {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: .08 });
    document.querySelectorAll('.cx-rv, .cx-rail, [data-cx-bars]').forEach(function (el) {
      if (el.classList.contains('cx-rail')) {
        Array.prototype.forEach.call(el.children, function (c, i) { c.style.setProperty('--i', Math.min(i, 4)); });
      }
      if (REDUCED) el.classList.add('is-in'); else io.observe(el);
    });
    onCleanup(function () { io.disconnect(); });
  }

  /* ---------------- hero ---------------- */
  function hero() {
    var gsap = window.gsap, ST = window.ScrollTrigger;
    if (REDUCED || !gsap || !ST) return;
    gsap.registerPlugin(ST);
    var tweens = [];
    document.querySelectorAll('.cx-hero').forEach(function (h) {
      var obj = h.querySelector('.cx-hero-obj');
      if (!obj) return;
      tweens.push(gsap.to(obj, {
        yPercent: -10, scale: 1.14, ease: 'none',
        scrollTrigger: { trigger: h, start: 'top top', end: 'bottom top', scrub: .4 }
      }));
    });
    onCleanup(function () { tweens.forEach(function (t) { t.scrollTrigger && t.scrollTrigger.kill(); t.kill(); }); });
  }

  /* ---------------- films ---------------- */
  function setPlay(card, playing) {
    var b = card.querySelector('[data-cx-play]');
    if (b) b.setAttribute('aria-pressed', playing ? 'true' : 'false');
  }
  function ensureSrc(v) { if (!v.getAttribute('src') && v.dataset.src) v.src = v.dataset.src; }
  function films() {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var card = e.target, v = card.querySelector('video');
        if (!v) return;
        if (e.isIntersecting) {
          if (card.dataset.userPaused || REDUCED) return;
          ensureSrc(v);
          v.play().then(function () { setPlay(card, true); }).catch(function () { setPlay(card, false); });
        } else if (!v.paused) { v.pause(); setPlay(card, false); }
      });
    }, { rootMargin: '150px 0px', threshold: .2 });
    document.querySelectorAll('[data-cx-film]').forEach(function (c) { if (REDUCED) setPlay(c, false); io.observe(c); });
    onCleanup(function () { io.disconnect(); });
  }

  /* ---------------- phone action bar ---------------- */
  function bar() {
    var b = document.querySelector('[data-cx-bar]');
    if (!b) return;
    var heroAct = document.querySelector('[data-cx-hero-act]'), foot = document.querySelector('footer');
    var st = { hero: !!heroAct, foot: false };
    var apply = function () { b.classList.toggle('is-hidden', st.hero || st.foot); };
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.target === heroAct) st.hero = e.isIntersecting;
        if (e.target === foot) st.foot = e.isIntersecting;
      });
      apply();
    });
    if (heroAct) io.observe(heroAct);
    if (foot) io.observe(foot);
    apply();
    onCleanup(function () { io.disconnect(); });
  }

  /* ---------------- deck ---------------- */
  function decks() {
    document.querySelectorAll('[data-cx-deck]').forEach(function (deck) {
      var cards = Array.prototype.slice.call(deck.querySelectorAll('.cx-dcard'));
      if (!cards.length) return;
      var order = cards.map(function (_, i) { return i; });
      var counter = deck.querySelector('[data-cx-deck-n]');
      var busy = false;

      function layout() {
        order.forEach(function (ci, pos) {
          cards[ci].dataset.pos = pos < 3 ? String(pos) : 'hide';
          cards[ci].setAttribute('aria-hidden', pos === 0 ? 'false' : 'true');
        });
        if (counter) counter.textContent = ('0' + (order[0] + 1)).slice(-2);
      }
      function settle(card) {
        card.classList.add('is-drag');
        card.style.transform = ''; card.style.opacity = '';
        layout();
        void card.offsetWidth;
        card.classList.remove('is-drag');
      }
      function go(dir, fromDx) {
        if (busy) return;
        busy = true;
        if (dir > 0) {
          var top = cards[order[0]], sign = fromDx ? Math.sign(fromDx) : -1;
          top.style.transition = 'transform .4s var(--e), opacity .4s';
          top.style.transform = 'translateX(' + (sign * 130) + '%) rotate(' + (sign * 16) + 'deg)';
          top.style.opacity = '0';
          setTimeout(function () {
            top.style.transition = '';
            order.push(order.shift());
            settle(top);
            busy = false;
          }, REDUCED ? 0 : 380);
        } else {
          var last = cards[order[order.length - 1]];
          last.classList.add('is-drag');
          last.style.transform = 'translateX(-130%) rotate(-16deg)';
          last.style.opacity = '0';
          order.unshift(order.pop());
          layout();
          void last.offsetWidth;
          last.classList.remove('is-drag');
          last.style.transform = ''; last.style.opacity = '';
          setTimeout(function () { busy = false; }, 300);
        }
      }
      deck._cxGo = go;

      /* drag / swipe the top card */
      var drag = null;
      function down(e) {
        var card = e.target.closest('.cx-dcard');
        if (!card || card.dataset.pos !== '0' || busy) return;
        drag = { card: card, x: e.clientX, y: e.clientY, dx: 0, id: e.pointerId, moved: false };
      }
      function move(e) {
        if (!drag || e.pointerId !== drag.id) return;
        var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
        if (!drag.moved) {
          if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) {
            if (Math.abs(dy) > 12) drag = null;   /* a vertical scroll, let it go */
            return;
          }
          drag.moved = true;
          drag.card.classList.add('is-drag');
          try { drag.card.setPointerCapture(e.pointerId); } catch (_) {}
        }
        drag.dx = dx;
        drag.card.style.transform = 'translateX(' + dx + 'px) rotate(' + (dx / 18) + 'deg)';
      }
      function up() {
        if (!drag) return;
        var d = drag; drag = null;
        if (!d.moved) return;
        d.card.classList.remove('is-drag');
        if (Math.abs(d.dx) > 70) go(1, d.dx);
        else { d.card.style.transform = ''; }
      }
      deck.addEventListener('pointerdown', down);
      deck.addEventListener('pointermove', move);
      deck.addEventListener('pointerup', up);
      deck.addEventListener('pointercancel', up);
      layout();
      onCleanup(function () {
        deck.removeEventListener('pointerdown', down);
        deck.removeEventListener('pointermove', move);
        deck.removeEventListener('pointerup', up);
        deck.removeEventListener('pointercancel', up);
      });
    });
  }

  /* ---------------- wipe ---------------- */
  function rnd(a, b) { return a + Math.random() * (b - a); }
  function paintDirt(ctx, w, h) {
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    var g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#8E7C69'); g.addColorStop(.5, '#7D6C5B'); g.addColorStop(1, '#8F7F6E');
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    var i, x, y, r, rg;
    for (i = 0; i < 160; i++) {                       /* blotches */
      x = rnd(0, w); y = rnd(0, h); r = rnd(14, Math.max(40, w * .16));
      rg = ctx.createRadialGradient(x, y, 0, x, y, r);
      var dark = Math.random() < .55;
      rg.addColorStop(0, dark ? 'rgba(62,48,36,' + rnd(.18, .4) + ')' : 'rgba(176,160,140,' + rnd(.12, .3) + ')');
      rg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.lineCap = 'round';
    for (i = 0; i < 9; i++) {                          /* scuffs */
      ctx.strokeStyle = 'rgba(55,42,32,' + rnd(.12, .25) + ')';
      ctx.lineWidth = rnd(3, 10);
      ctx.beginPath(); x = rnd(0, w); y = rnd(0, h); ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + rnd(-80, 80), y + rnd(-80, 80), x + rnd(-140, 140), y + rnd(-140, 140));
      ctx.stroke();
    }
    for (i = 0; i < Math.round(w * h / 90); i++) {    /* grit */
      ctx.fillStyle = Math.random() < .5 ? 'rgba(40,30,22,.35)' : 'rgba(210,198,182,.25)';
      ctx.fillRect(rnd(0, w), rnd(0, h), rnd(1, 2.4), rnd(1, 2.4));
    }
  }
  function wipes() {
    document.querySelectorAll('[data-cx-wipe]').forEach(function (card) {
      var cv = card.querySelector('canvas');
      if (!cv || !cv.getContext) return;
      var ctx = cv.getContext('2d');
      var again = card.querySelector('[data-cx-wipe-again]');
      var W = 0, H = 0, dpr = 1, brush = 60, grid, GX = 24, GY = 30, cleared = 0, done = false, last = null, swept = false;

      function reset() {
        var r = card.getBoundingClientRect();
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        W = r.width; H = r.height;
        cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        paintDirt(ctx, W, H);
        brush = Math.max(54, W * .2);
        grid = new Uint8Array(GX * GY); cleared = 0; done = false;
        cv.classList.remove('is-done'); card.classList.remove('is-clean', 'is-wiping');
        if (again) again.hidden = true;
      }
      function mark(x, y) {
        var rx = brush / 2 / W * GX, ry = brush / 2 / H * GY, cx = x / W * GX, cy = y / H * GY;
        for (var gy = Math.max(0, Math.floor(cy - ry)); gy <= Math.min(GY - 1, Math.ceil(cy + ry)); gy++) {
          for (var gx = Math.max(0, Math.floor(cx - rx)); gx <= Math.min(GX - 1, Math.ceil(cx + rx)); gx++) {
            var k = gy * GX + gx;
            if (!grid[k]) { grid[k] = 1; cleared++; }
          }
        }
      }
      function stroke(a, b) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = brush;
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        var steps = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / (brush / 3)));
        for (var s = 0; s <= steps; s++) mark(a.x + (b.x - a.x) * s / steps, a.y + (b.y - a.y) * s / steps);
        if (!done && cleared / (GX * GY) > .55) finish();
      }
      function finish() {
        done = true;
        cv.classList.add('is-done');
        card.classList.add('is-clean'); card.classList.remove('is-wiping');
        if (again) again.hidden = false;
      }
      function pt(e) { var r = cv.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
      function down(e) {
        if (done) return;
        last = pt(e); card.classList.add('is-wiping');
        try { cv.setPointerCapture(e.pointerId); } catch (_) {}
        stroke(last, { x: last.x + .1, y: last.y });
      }
      function move(e) { if (!last) return; var p = pt(e); stroke(last, p); last = p; }
      function up() { last = null; }
      cv.addEventListener('pointerdown', down);
      cv.addEventListener('pointermove', move);
      cv.addEventListener('pointerup', up);
      cv.addEventListener('pointercancel', up);

      /* the first time it comes into view, sweep one stripe by itself */
      var io = new IntersectionObserver(function (en) {
        en.forEach(function (e) {
          if (!e.isIntersecting || swept || REDUCED) return;
          swept = true; io.disconnect();
          var t0 = null, from = { x: -brush, y: H * .3 }, to = { x: W + brush, y: H * .44 }, prev = from;
          (function step(t) {
            if (done || last) return;
            if (!t0) t0 = t;
            var k = Math.min(1, (t - t0) / 1100), e2 = 1 - Math.pow(1 - k, 3);
            var p = { x: from.x + (to.x - from.x) * e2, y: from.y + (to.y - from.y) * e2 + Math.sin(e2 * Math.PI) * -18 };
            stroke(prev, p); prev = p;
            if (k < 1) requestAnimationFrame(step);
          })(performance.now());
          setTimeout(function () {}, 0);
        });
      }, { threshold: .55 });
      io.observe(card);

      var lastW = 0;
      var ro = new ResizeObserver(function () {
        var w = card.getBoundingClientRect().width;
        if (Math.abs(w - lastW) > 2) { lastW = w; reset(); }
      });
      ro.observe(card);

      card._cxReset = reset;
      onCleanup(function () {
        io.disconnect(); ro.disconnect();
        cv.removeEventListener('pointerdown', down); cv.removeEventListener('pointermove', move);
        cv.removeEventListener('pointerup', up); cv.removeEventListener('pointercancel', up);
      });
    });
  }

  /* ---------------- story ----------------
     "Our story" as a phone story. One chapter on at a time; its bar
     fills while it runs. Words/photo chapters run data-sec seconds; a
     clip chapter runs for its clip (until the clip's length is known,
     or if it never loads, it runs on the timer so the story can't
     stall). It runs only while half on screen and the tab is visible.
     Hold to pause; swipe or tap to move. Reduced motion: clips still
     play when you reach them, but nothing turns the page for you. */
  function stories() {
    document.querySelectorAll('[data-cx-story]').forEach(function (st) {
      var player = st.querySelector('.cx-st-player');
      var slides = Array.prototype.slice.call(st.querySelectorAll('.cx-st-slide'));
      if (!player || !slides.length) return;
      var fills = st.querySelectorAll('.cx-st-bars i');
      var nEl = st.querySelector('[data-cx-st-n]');
      var steps = st.querySelectorAll('[data-cx-st-to]');
      var snd = st.querySelector('[data-cx-st-sound]');
      var again = st.querySelector('[data-cx-st-again]');
      var SEC = (Number(st.dataset.sec) || 6) * 1000;
      var cur = 0, t0 = 0, elapsed = 0, raf = 0, inView = false, held = false, done = false, muted = true;

      function vid(i) { return slides[i].querySelector('video'); }
      function len(i) { var v = vid(i); return v && v.duration > 0 && isFinite(v.duration) ? v.duration * 1000 : SEC; }
      function paint() {
        var p = Math.min(1, elapsed / len(cur));
        Array.prototype.forEach.call(fills, function (f, k) {
          f.style.width = k < cur ? '100%' : k > cur ? '0%' : (p * 100).toFixed(2) + '%';
        });
      }
      function stopTick() { if (raf) cancelAnimationFrame(raf); raf = 0; }
      function resume() {
        stopTick();
        var v = vid(cur);
        var live = inView && !held && !done && !document.hidden;
        if (v) { if (live) v.play().catch(function () {}); else if (!v.paused) v.pause(); }
        if (!live || (REDUCED && !v)) return;
        t0 = performance.now() - elapsed;
        raf = requestAnimationFrame(tick);
      }
      function tick(now) {
        var v = vid(cur);
        if (v && v.duration > 0) {
          elapsed = v.currentTime * 1000;
          if (v.ended) { if (!REDUCED) return next(); stopTick(); paint(); return; }
        } else {
          elapsed = now - t0;
          if (elapsed >= SEC && !REDUCED) return next();
        }
        paint();
        raf = requestAnimationFrame(tick);
      }
      function show(i) {
        i = Math.max(0, Math.min(slides.length - 1, i));
        var old = vid(cur);
        if (old) { old.pause(); try { old.currentTime = 0; } catch (_) {} }
        cur = i; elapsed = 0; done = false;
        slides.forEach(function (s, k) {
          s.classList.toggle('is-on', k === i);
          s.setAttribute('aria-hidden', k === i ? 'false' : 'true');
        });
        player.classList.toggle('is-dark', !slides[i].classList.contains('cx-st-slide--txt'));
        if (nEl) nEl.textContent = ('0' + (i + 1)).slice(-2);
        Array.prototype.forEach.call(steps, function (b, k) {
          if (k === i) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
          b.classList.toggle('is-seen', k < i);
        });
        if (again) again.hidden = true;
        var v = vid(i);
        if (snd) snd.hidden = !v;
        if (v) { ensureSrc(v); v.muted = muted; try { v.currentTime = 0; } catch (_) {} }
        paint();
        resume();
      }
      function next() { if (cur < slides.length - 1) show(cur + 1); else finish(); }
      function finish() {
        done = true; stopTick();
        elapsed = len(cur); paint();
        var v = vid(cur); if (v) v.pause();
        if (again) again.hidden = false;
      }

      /* hold to pause, swipe to move; a hold or swipe eats the click */
      var p = null, holdT = 0;
      function down(e) {
        st._cxEat = false;
        if (e.target.closest('[data-cx-st-sound], [data-cx-st-again]')) return;
        p = { x: e.clientX, y: e.clientY, id: e.pointerId, moved: false };
        holdT = setTimeout(function () { held = true; player.classList.add('is-held'); resume(); }, 220);
      }
      function move(e) {
        if (!p || e.pointerId !== p.id) return;
        if (Math.abs(e.clientX - p.x) > 10 || Math.abs(e.clientY - p.y) > 10) { p.moved = true; clearTimeout(holdT); }
      }
      function up(e) {
        clearTimeout(holdT);
        if (!p) return;
        var dx = e.clientX - p.x, dy = e.clientY - p.y, wasHeld = held;
        p = null;
        if (held) { held = false; player.classList.remove('is-held'); resume(); }
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { st._cxEat = true; dx < 0 ? next() : show(cur - 1); }
        else if (wasHeld) st._cxEat = true;
      }
      function cancel() { clearTimeout(holdT); p = null; if (held) { held = false; player.classList.remove('is-held'); resume(); } }
      player.addEventListener('pointerdown', down);
      player.addEventListener('pointermove', move);
      player.addEventListener('pointerup', up);
      player.addEventListener('pointercancel', cancel);

      var io = new IntersectionObserver(function (en) { inView = en[0].isIntersecting; resume(); }, { threshold: .5 });
      io.observe(player);
      function vis() { resume(); }
      document.addEventListener('visibilitychange', vis);

      st._cx = {
        go: function (d) { if (d > 0) next(); else show(cur - 1); },
        to: show,
        again: function () { show(0); },
        sound: function () {
          muted = !muted;
          var v = vid(cur); if (v) { v.muted = muted; if (!muted) resume(); }
          if (snd) snd.setAttribute('aria-pressed', muted ? 'false' : 'true');
        }
      };
      show(0);
      onCleanup(function () {
        stopTick(); io.disconnect(); clearTimeout(holdT);
        document.removeEventListener('visibilitychange', vis);
        player.removeEventListener('pointerdown', down); player.removeEventListener('pointermove', move);
        player.removeEventListener('pointerup', up); player.removeEventListener('pointercancel', cancel);
        slides.forEach(function (s) { var v = s.querySelector('video'); if (v) v.pause(); });
      });
    });
  }

  /* ---------------- delegated clicks ---------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    var play = t.closest('[data-cx-play]');
    if (play) {
      var card = play.closest('[data-cx-film]'), v = card && card.querySelector('video');
      if (!v) return;
      ensureSrc(v);
      if (v.paused) { delete card.dataset.userPaused; v.play().catch(function () {}); setPlay(card, true); }
      else { card.dataset.userPaused = '1'; v.pause(); setPlay(card, false); }
      return;
    }
    var snd = t.closest('[data-cx-sound]');
    if (snd) {
      var c2 = snd.closest('[data-cx-film]'), v2 = c2 && c2.querySelector('video');
      if (!v2) return;
      ensureSrc(v2);
      v2.muted = !v2.muted;
      if (!v2.muted) v2.currentTime = 0;
      delete c2.dataset.userPaused;
      v2.play().catch(function () {});
      setPlay(c2, true);
      var label = snd.querySelector('span');
      if (label) label.textContent = v2.muted ? snd.dataset.on : snd.dataset.off;
      return;
    }
    var sto = t.closest('[data-cx-st-go], [data-cx-st-to], [data-cx-st-again], [data-cx-st-sound]');
    if (sto) {
      var story = sto.closest('[data-cx-story]'), api = story && story._cx;
      if (!api) return;
      if (sto.hasAttribute('data-cx-st-sound')) api.sound();
      else if (sto.hasAttribute('data-cx-st-again')) api.again();
      else if (sto.hasAttribute('data-cx-st-to')) api.to(Number(sto.dataset.cxStTo));
      else if (story._cxEat) story._cxEat = false;
      else api.go(Number(sto.dataset.cxStGo));
      return;
    }
    var dk = t.closest('[data-cx-deck-go]');
    if (dk) {
      var deck = dk.closest('[data-cx-deck]');
      if (deck && deck._cxGo) deck._cxGo(Number(dk.dataset.cxDeckGo));
      return;
    }
    var again = t.closest('[data-cx-wipe-again]');
    if (again) {
      var wc = again.closest('[data-cx-wipe]');
      if (wc && wc._cxReset) wc._cxReset();
      return;
    }
    var arrow = t.closest('[data-cx-rail]');
    if (arrow) {
      var sec = arrow.closest('.cx'), rail = sec && sec.querySelector('.cx-rail');
      if (!rail) return;
      var first = rail.firstElementChild;
      var step = first ? first.getBoundingClientRect().width + 20 : rail.clientWidth * .8;
      rail.scrollBy({ left: step * Number(arrow.dataset.cxRail), behavior: REDUCED ? 'auto' : 'smooth' });
    }
  });

  function build() {
    cleanups.forEach(function (fn) { try { fn(); } catch (_) {} });
    cleanups = [];
    reveals(); hero(); films(); bar(); decks(); wipes(); stories();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build);
  else build();
  document.addEventListener('shopify:section:load', build);
  document.addEventListener('shopify:section:unload', function () { setTimeout(build, 0); });
})();
